/**
 * Ensures that the specified path exists in the object.
 * If any part of the path is undefined, it will be initialized to an empty object `{}`.
 */
export function ensureObjectPath(
  obj: Record<string, unknown>,
  path: string[],
): Record<string, unknown> {
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (key === undefined) continue;
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  return obj;
}

/**
 * Sets a value at a specified path in an object.
 * If any part of the path is undefined, it will be initialized to an empty object `{}`.
 * Creates new objects along the path to ensure Vue reactivity is triggered.
 */
export function setObjectPathValue(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  if (path.length === 0) return obj;

  const firstKey = path[0];
  if (firstKey === undefined) return obj;

  if (path.length === 1) {
    obj[firstKey] = value;
    return obj;
  }

  // Create new nested structure to trigger Vue reactivity
  const [, ...rest] = path;
  obj[firstKey] = setObjectPathValue(
    { ...((obj[firstKey] as Record<string, unknown>) || {}) }, // Shallow clone to trigger reactivity
    rest,
    value,
  );
  return obj;
}

/**
 * Retrieves the value at a specified path in an object.
 * If any part of the path is undefined, it will return undefined.
 */
export function getObjectPathValue<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  path: string[],
): T | undefined {
  if (!obj) return undefined;
  let current: Record<string, unknown> | unknown = obj;
  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (key === undefined) return undefined;
    if (
      typeof current !== "object" ||
      current === null ||
      !(key in (current as Record<string, unknown>))
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current as T;
}
